import { useId, useState, type FormEvent } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { authClient, normalizePhoneInput } from '../../lib/auth-client'
import { normalizeOtpInput } from '../../lib/auth-input'
import { useOtpResend } from './use-otp-resend'

interface PhoneOtpFormProps {
  locale: 'ar' | 'en' | 'de' | 'nl'
  defaultPhone?: string
  onVerified: () => void | Promise<void>
  submitLabel?: string
}

const copy = {
  ar: {
    phone: 'رقم الجوال', phoneHint: '050 123 4567', phoneHelp: <>للجوال السعودي: ابدأ بـ <bdi dir="ltr">05</bdi> أو <bdi dir="ltr">+966</bdi>. للأرقام الأخرى، أضف رمز الدولة.</>, send: 'إرسال رمز التحقق',
    code: 'رمز التحقق', verify: 'التحقق والمتابعة', resend: 'إرسال رمز جديد',
    sent: 'أرسلنا رمزًا من 6 أرقام إلى جوالك.', generic: 'تعذّر إكمال الطلب. أعد المحاولة.',
    change: 'استخدام رقم آخر', retry: 'إعادة المحاولة', throttled: 'تجاوزت الحد المسموح للمحاولات. انتظر قليلًا ثم أعد المحاولة.',
  },
  en: {
    phone: 'Mobile number', phoneHint: '+966 50 123 4567', phoneHelp: 'Saudi numbers can start with 05 or +966. For other numbers, include the country code.', send: 'Send verification code',
    code: 'Verification code', verify: 'Verify and continue', resend: 'Send a new code',
    sent: 'We sent a six-digit code to your mobile.', generic: 'We could not complete that request. Try again.',
    change: 'Use a different number', retry: 'Try again', throttled: 'Too many requests. Please wait before trying again.',
  },
  de: {
    phone: 'Mobilnummer', phoneHint: '+966 50 123 4567', phoneHelp: 'Saudische Nummern beginnen mit 05 oder +966. Für andere Nummern bitte die Ländervorwahl angeben.', send: 'Bestätigungscode senden',
    code: 'Bestätigungscode', verify: 'Bestätigen und weiter', resend: 'Neuen Code senden',
    sent: 'Wir haben einen sechsstelligen Code gesendet.', generic: 'Das hat nicht funktioniert. Bitte versuche es erneut.',
    change: 'Andere Nummer verwenden', retry: 'Erneut versuchen', throttled: 'Zu viele Anfragen. Bitte warte, bevor du es erneut versuchst.',
  },
  nl: {
    phone: 'Mobiel nummer', phoneHint: '+966 50 123 4567', phoneHelp: 'Saoedische nummers mogen beginnen met 05 of +966. Voeg bij andere nummers de landcode toe.', send: 'Verificatiecode sturen',
    code: 'Verificatiecode', verify: 'Verifiëren en doorgaan', resend: 'Nieuwe code sturen',
    sent: 'We hebben een zescijferige code gestuurd.', generic: 'Dit is niet gelukt. Probeer het opnieuw.',
    change: 'Ander nummer gebruiken', retry: 'Opnieuw proberen', throttled: 'Te veel verzoeken. Wacht even voordat je het opnieuw probeert.',
  },
} as const

export function PhoneOtpForm({ locale, defaultPhone = '', onVerified, submitLabel }: PhoneOtpFormProps) {
  const text = copy[locale]
  const phoneHelpId = useId()
  const [phone, setPhone] = useState(defaultPhone)
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'phone' | 'code' | 'authenticated' | 'verified'>('phone')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const resend = useOtpResend()

  const sendCode = async () => {
    if (pending || resend.seconds > 0) return
    setPending(true)
    setError('')
    const normalized = normalizePhoneInput(phone)
    try {
      const result = await authClient.phoneNumber.sendOtp({ phoneNumber: normalized }, {
        headers: { 'Accept-Language': locale === 'ar' ? 'ar' : 'en' },
      })
      if (result.error) { setError(result.error.status === 429 ? text.throttled : text.generic); return }
      setPhone(normalized)
      setCode('')
      setStep('code')
      resend.start()
    } catch {
      setError(text.generic)
    } finally {
      setPending(false)
    }
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (pending || step === 'verified') return
    if (step === 'phone') return sendCode()
    setPending(true)
    setError('')
    let authenticated = step === 'authenticated'
    try {
      if (step !== 'authenticated') {
        const result = await authClient.phoneNumber.verify({ phoneNumber: phone, code })
        if (result.error) { setError(result.error.status === 429 ? text.throttled : text.generic); return }
        setStep('authenticated')
        authenticated = true
      }
      await onVerified()
      setStep('verified')
    } catch (value) {
      setError(authenticated && value instanceof Error ? value.message : text.generic)
    } finally {
      setPending(false)
    }
  }

  return <form onSubmit={submit} className="space-y-4">
    <label className="block text-sm font-semibold">{text.phone}
      <Input name="phoneNumber" value={phone} onChange={(event) => setPhone(event.target.value)} type="tel" inputMode="tel"
        autoComplete="tel" dir="ltr" className="mt-2" placeholder={text.phoneHint} aria-describedby={phoneHelpId} disabled={step !== 'phone' || pending} required />
    </label>
    <p id={phoneHelpId} className="text-sm text-muted">{text.phoneHelp}</p>
    {step === 'code' && <>
      <p className="text-sm text-muted" role="status">{text.sent}</p>
      <label className="block text-sm font-semibold">{text.code}
        <Input name="verificationCode" value={code} onChange={(event) => setCode(normalizeOtpInput(event.target.value))}
          inputMode="numeric" autoComplete="one-time-code" dir="ltr" className="mt-2 text-center tracking-[0.35em]" minLength={6} maxLength={6} disabled={pending} required autoFocus />
      </label>
    </>}
    {error && <p role="alert" className="text-sm text-danger">{error}</p>}
    <Button type="submit" className="w-full" size="lg" disabled={pending || step === 'verified' || (step === 'phone' && resend.seconds > 0)}>
      {step === 'authenticated' ? text.retry : submitLabel && step === 'code' ? submitLabel : step === 'phone' ? `${text.send}${resend.seconds > 0 ? ` (${resend.seconds})` : ''}` : text.verify}
    </Button>
    {step === 'code' && <button type="button" disabled={pending || resend.seconds > 0}
      className="min-h-11 w-full text-sm font-semibold underline underline-offset-4 disabled:opacity-50" onClick={() => void sendCode()}>
      {text.resend}{resend.seconds > 0 ? ` (${resend.seconds})` : ''}
    </button>}
    {(step === 'code' || step === 'authenticated') && <button type="button" disabled={pending} className="min-h-11 w-full text-sm font-semibold underline underline-offset-4"
      onClick={() => { setStep('phone'); setCode(''); setError('') }}>{text.change}</button>}
  </form>
}
